define('util/errorCallbackFactory',[
    'lib/lodash4',
    'lib/knockout',
    'services/authService',
    'services/loggingService',
    'services/toastService',
    'extensions/i18n/knockout.i18n'
], function (_, ko, authService, LOG, toasts) {
    'use strict';

    /*
        Before showing a message we need to make sure there is a key defined in the namespace. Otherwise we will
        use the message in the default namespace, which will always exist.
     */
    var showMessage = function (namespaces, key, code, fieldErrorCode, field) {
        var errorKey = errorCallbackFactory.findErrorMessageKey(namespaces, key, code, fieldErrorCode, field);
        toasts.notifyMajorAlert(errorKey);
    };

    /**
     * These are the default error handlers. This object can be overridden or extended as needed when you get an
     * instance of the error callback from the factory.
     */
    var DEFAULT_STRATEGIES = {
        "401": {
            //Three different error codes are all treated as expired sessions on the client
            "INVALID_SESSION_HEADER": function (namespaces) {
                showMessage(namespaces, 'expired');
            },
            "SESSION_HEADER_REQUIRED": function (namespaces) {
                showMessage(namespaces, 'expired');
            },
            "SESSION_HEADER_EXPIRED": function (namespaces) {
                showMessage(namespaces, 'expired');
            }
        },
        "403": {
            "DEFAULT": function (namespaces) {
                showMessage(namespaces, 'forbidden');
            }
        },
        "429": {
            "DEFAULT": function (namespaces) {
                showMessage(namespaces, 'generalRateLimit');
            }
        },
        "TIMEOUT": function (namespaces) {
            showMessage(namespaces, 'timeout');
        },
        "DEFAULT": function (namespaces, httpStatus, errorCode, fieldErrorCode, field) {
            showMessage(namespaces, httpStatus, errorCode, fieldErrorCode, field);
        }
    };

    var findHttpStrategy = function (strategies, httpStatus, errorCode) {
        if (strategies[httpStatus] && _.isFunction(strategies[httpStatus])) {
            return strategies[httpStatus];
        }

        if (strategies[httpStatus] && strategies[httpStatus][errorCode] && _.isFunction(strategies[httpStatus][errorCode])) {
            return strategies[httpStatus][errorCode];
        }

        if (strategies[httpStatus] && strategies[httpStatus].DEFAULT && _.isFunction(strategies[httpStatus].DEFAULT)) {
            return strategies[httpStatus].DEFAULT;
        }

        return strategies.DEFAULT;
    };

    var errorHandler = function (httpError, strategies, namespaces) {

        // parse the error and get all the details from it
        var httpStatus, errorCode, fieldErrorCode, field;
        if((httpError.type === "http") && (httpError.details)) {
            httpStatus = httpError.details.status;

            if(httpError.details.response) {
                errorCode = httpError.details.response.code;

                if(httpError.details.response.details && httpError.details.response.details.length > 0 && httpError.details.response.details[0]) {
                    // Specific views can choose to extract all the error details if they wish. Here we're just adding the base functionality
                    // of showing an error toast for the first error detail. I considered getting all the error details, but either showing
                    // multiple toasts or one gigantic toast with potentially many errors in it didn't sound like a good option.
                    fieldErrorCode = httpError.details.response.details[0].errorCode;
                    field = httpError.details.response.details[0].fieldName;
                }
            }
        }

        // find the most specific strategy to the error
        var strategy;
        if (httpError.type === "timeout") {
            strategy = strategies.TIMEOUT;
        } else {
            strategy = findHttpStrategy(strategies, httpStatus, errorCode);
        }

        strategy(namespaces, httpStatus, errorCode, fieldErrorCode, field);
    };

    /**
     * SCENARIO 1: CUSTOM MESSAGE KEYS
     *
     * By setting the namespace option, each error notification will use a unique message key.
     *
     * errorCallbackFactory.createCallback({ "namespace": "custom.error" });
     *
     * If you did this, you would need to provide some localized strings all the error status and code combinations
     * that you wanted to handle.
     *
     * custom.error.400.CUSTOM_STATUS -> handles api errors with status 400 and errorCode of "CUSTOM_STATUS"
     * custom.error.400 -> handles api errors with status 400 that don't match a more specific string
     *
     *
     * SCENARIO 2: CUSTOM PRESENTATION LOGIC
     *
     * By changing the strategy for an individual error, you can change the presentation logic for the error.
     *
     * errorCallbackFactory.createCallback({
     *        "namespace": "custom.error",
     *        "strategies": {
     *            "400": function (namespaces, httpStatus, errorCode) {
     *                // Handles all 400 status errors
     *                // You could modify the containing view model here... instead of using the notification view model.
     *            },
     *            "403": {
     *                "FORBIDDEN": function (namespaces, httpStatus, errorCode) {
     *                    // Handles 403 status errors with error code 'FORBIDDEN'
     *                },
     *                "DEFAULT": function (namespaces, httpStatus, errorCode) {
     *                   // Handles any 403 status errors that don't have an error code strategy
     *                }
     *            },
     *            "TIMEOUT": function (namespaces) {
     *                //Handles timeouts
     *            },
     *            "DEFAULT": function (namespaces, httpStatus, errorCode) {
     *                // Handles any unmatched status
     *            }
     *        }
     *     }
     *
     * NOTE: The base functionality will persist for strategies not overridden. For example, if you don't override the
     * strategy for "unauthorized" the callback will continue to return the user to the login screen when the session
     * expires.
     *
     * @exports util/errorCallbackFactory
     */
    var errorCallbackFactory = {
        findErrorMessageKey: function (namespaces, httpStatus, code, fieldErrorCode, field) {

            // the more specific error strings need to be earlier in the array so that they're looked for first
            var keys = [];

            // add each namespace's error strings
            _.forEach(namespaces, function(namespace){
                if(fieldErrorCode && field) {
                    //namespace key field code
                    // e.g. "config.400.DUPLICATE_VALUE.label"
                    // e.g. "config.400.DUPLICATE_VALUE" (if the field sent down is 'NO_FIELD')
                    keys.push(namespace + '.' + httpStatus + '.' + fieldErrorCode + (field === 'NO_FIELD' ? '' : '.' + field));
                }
                keys.push(namespace + '.' + httpStatus + '.' + code);   //namespace key code  e.g. "config.400.GENERAL_BAD_REQUEST"
                keys.push(namespace + '.' + httpStatus);                //namespace key  e.g. "config.400"
                keys.push(namespace + '.error.default');                //namespace default  e.g. "config.error.default"
                keys.push(namespace);                                   //namespace itself  e.g. "config"
            });

            var validMessageKey = _.find(keys, function (value) {
                return ko.i18n.keyExists(value);
            });

            return validMessageKey || 'error.default';
        },

        createCallback: function (options) {

            // default the logError
            options = _.assign({ logError: true }, options);

            // default the namespaces. if no namespaces sent in, check the 'namespace' attribute, else default to 'error'
            var defaultNamespace = options.namespace || "error";
            options = _.assign({ namespaces: [defaultNamespace] }, options);

            // default the strategies
            options.strategies = options.strategies || DEFAULT_STRATEGIES;

            return function (error) {
                errorHandler(error, options.strategies, options.namespaces);
                if (options.logError && (error instanceof Error)) {
                    if (options.logCallback && _.isFunction(options.logCallback)){
                        options.logCallback(error.stack || error.message);
                    } else {
                        LOG.error(error.stack || error.message);
                    }
                }
                return error;
            };
        }
    };

    return errorCallbackFactory;
});

